class ProductsBundleSelection extends HTMLElement {
  constructor() {
    super();
    this.maxItems = parseInt(this.dataset.maxItems, 10) || 3;
    this.preventDuplicates = this.dataset.preventDuplicateItems === 'true';
    this.selections = new Map();
    this.moneyFormat = window.Shopify && window.Shopify.currency
      ? window.Shopify.currency.active
      : 'USD';
  }

  connectedCallback() {
    this.grid = this.querySelector('.bundle-grid');
    this.slots = Array.from(this.querySelectorAll('[data-bundle-slot]'));
    this.totalEl = this.querySelector('[data-bundle-total]');
    this.countEls = Array.from(this.querySelectorAll('[data-bundle-count]'));
    this.ctaButton = this.querySelector('[data-bundle-add-to-cart]');
    this.floatingToggle = this.querySelector('[data-bundle-floating-toggle]');
    this.summaryPanel = this.querySelector('.bundle-summary');

    this.addToCartLabel = this.dataset.addToCartLabel || 'Add bundle to cart';
    this.addMoreLabel = this.dataset.addMoreLabel || 'Add %%count%% more item';
    this.addMoreLabelPlural = this.dataset.addMoreLabelPlural || 'Add %%count%% more items';
    this.limitMessage = this.dataset.limitMessage || 'You can only add a maximum of %%max%% products in the bundle.';
    this.duplicateMessage = this.dataset.duplicateMessage || 'This product is already in the bundle.';

    this.bindCardButtons();
    this.bindVariantSelects();
    this.bindCta();
    this.bindFloatingToggle();
    this.updateUI();
  }

  bindCardButtons() {
    this.grid.querySelectorAll('[data-bundle-add-button]').forEach((button) => {
      button.addEventListener('click', () => this.toggleCard(button));
    });
  }

  bindVariantSelects() {
    this.grid.querySelectorAll('[data-bundle-variant-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const card = select.closest('[data-bundle-card]');
        const button = card.querySelector('[data-bundle-add-button]');
        const option = select.options[select.selectedIndex];
        button.dataset.variantId = option.value;
        button.dataset.productPrice = option.dataset.price;

        const productId = card.dataset.productId;
        for (const [slotIndex, selection] of this.selections.entries()) {
          if (selection.productId === productId) {
            selection.variantId = option.value;
            selection.price = parseInt(option.dataset.price, 10);
            this.selections.set(slotIndex, selection);
          }
        }
        this.updateUI();
      });
    });
  }

  bindCta() {
    this.ctaButton.addEventListener('click', () => this.addBundleToCart());
  }

  bindFloatingToggle() {
    if (!this.floatingToggle) return;
    this.floatingToggle.addEventListener('click', () => {
      if (this.summaryPanel) {
        this.summaryPanel.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  toggleCard(button) {
    const card = button.closest('[data-bundle-card]');
    const productId = card.dataset.productId;

    const existingSlot = this.findSlotByProductId(productId);
    if (existingSlot !== null) {
      this.selections.delete(existingSlot);
      card.removeAttribute('data-bundle-selected');
      button.removeAttribute('data-bundle-selected');
      this.updateUI();
      return;
    }

    if (this.preventDuplicates && this.isProductSelected(productId)) {
      this.notify(this.duplicateMessage);
      return;
    }

    if (this.selections.size >= this.maxItems) {
      this.notify(this.limitMessage.replace('%%max%%', this.maxItems));
      return;
    }

    const variantId = button.dataset.variantId;
    const title = button.dataset.productTitle;
    const image = button.dataset.productImage;
    const price = parseInt(button.dataset.productPrice, 10) || 0;

    const nextSlot = this.findNextEmptySlot();
    this.selections.set(nextSlot, { productId, variantId, title, image, price });

    card.setAttribute('data-bundle-selected', 'true');
    button.setAttribute('data-bundle-selected', 'true');

    this.updateUI();
  }

  isProductSelected(productId) {
    return this.findSlotByProductId(productId) !== null;
  }

  findSlotByProductId(productId) {
    for (const [slotIndex, selection] of this.selections.entries()) {
      if (selection.productId === productId) return slotIndex;
    }
    return null;
  }

  findNextEmptySlot() {
    for (let i = 0; i < this.maxItems; i++) {
      if (!this.selections.has(i)) return i;
    }
    return this.selections.size;
  }

  updateUI() {
    const count = this.selections.size;
    const selectionValues = Array.from(this.selections.values());

    this.slots.forEach((slot, index) => {
      const selection = selectionValues[index];
      const isFreeSlot = slot.dataset.isFreeSlot === 'true';

      if (selection) {
        slot.setAttribute('data-filled', 'true');
        if (isFreeSlot) {
          // Keep the -100% badge visible, layered on top of the product image
          slot.innerHTML = `<img src="${selection.image}" alt="${selection.title}"><span class="bundle-slot__discount">-100%</span>`;
        } else {
          slot.innerHTML = `<img src="${selection.image}" alt="${selection.title}">`;
        }
      } else {
        slot.removeAttribute('data-filled');
        if (isFreeSlot) {
          slot.innerHTML = '<span class="bundle-slot__discount">-100%</span>';
        } else {
          slot.innerHTML = `<span class="bundle-slot__placeholder">${index + 1}</span>`;
        }
      }
    });

    this.countEls.forEach((el) => { el.textContent = count; });

    const total = selectionValues.reduce((sum, s) => sum + (s.price || 0), 0);
    if (this.totalEl) {
      this.totalEl.textContent = this.formatMoney(total);
    }

    if (count === 0) {
      this.ctaButton.disabled = true;
      this.ctaButton.textContent = this.addMoreLabelPlural.replace('%%count%%', this.maxItems);
    } else if (count < this.maxItems) {
      const remaining = this.maxItems - count;
      this.ctaButton.disabled = true;
      const label = remaining === 1 ? this.addMoreLabel : this.addMoreLabelPlural;
      this.ctaButton.textContent = label.replace('%%count%%', remaining);
    } else {
      this.ctaButton.disabled = false;
      this.ctaButton.textContent = this.addToCartLabel;
    }
  }

  formatMoney(cents) {
    try {
      return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: this.moneyFormat });
    } catch (e) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  notify(message) {
    window.alert(message);
  }

  async addBundleToCart() {
    if (this.selections.size < this.maxItems) return;

    this.ctaButton.disabled = true;
    const originalText = this.ctaButton.textContent;
    this.ctaButton.textContent = '...';

    const items = Array.from(this.selections.values()).map((selection) => ({
      id: selection.variantId,
      quantity: 1,
    }));

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) throw new Error('Cart add failed');

      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
    } catch (error) {
      console.error('[products-bundle-selection]', error);
      this.notify('Something went wrong adding the bundle to your cart. Please try again.');
      this.ctaButton.disabled = false;
      this.ctaButton.textContent = originalText;
    }
  }
}

customElements.define('products-bundle-selection', ProductsBundleSelection);